<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    :close-on-click-modal="false"
    :append-to-body="true"
    custom-class="login-dialog-panel"
    class="login-dialog"
    @close="handleClose"
    :show-close="true"
  >
    <div class="login-header">
      <div class="logo-container">
        <div class="logo-text">skyautomax</div>
        <h2 class="login-title">{{ dialogHeaderTitle }}</h2>
        <p class="login-subtitle">{{ dialogHeaderSubtitle }}</p>
      </div>
    </div>

    <ForgotPasswordForm
      v-if="dialogActiveTab === 'forgot'"
      ref="forgotPasswordForm"
      input-size="large"
      :initial-email="loginForm.username"
      @back="dialogActiveTab = 'login'"
      @success="handleForgotPasswordSuccess"
    />

    <el-tabs v-else v-model="dialogActiveTab" @tab-click="handleTabClick" class="login-tabs">
      <el-tab-pane :label="$t('auth.login')" name="login">
        <el-form
          :model="loginForm"
          :rules="loginRules"
          ref="loginForm"
          label-width="0"
          class="login-form"
        >
          <el-form-item prop="username">
            <el-input
              name="username"
              type="text"
              v-model="loginForm.username"
              autoComplete="on"
              :placeholder="$t('auth.usernameOrEmail')"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-user"></i>
              </span>
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              name="password"
              :type="loginPwdType"
              @keyup.enter.native="handleLogin"
              v-model="loginForm.password"
              autoComplete="on"
              :placeholder="$t('auth.password')"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-lock"></i>
              </span>
              <span slot="suffix" @click="toggleLoginPwd" class="password-toggle">
                <i :class="loginPwdType === 'password' ? 'el-icon-view' : 'el-icon-hide'"></i>
              </span>
            </el-input>
          </el-form-item>
          <div class="form-extra">
            <el-button type="text" class="forgot-btn" @click="dialogActiveTab = 'forgot'">
              {{ $t('auth.forgotPassword') }}
            </el-button>
          </div>
          <el-form-item>
            <el-button
              type="primary"
              size="large"
              style="width: 100%"
              :loading="loginLoading"
              @click.native.prevent="handleLogin"
              class="login-button"
            >
              {{ loginLoading ? $t('auth.loggingIn') : $t('auth.login') }}
            </el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
      <el-tab-pane :label="$t('auth.register')" name="register">
        <el-form
          :model="registerForm"
          :rules="registerRules"
          ref="registerForm"
          label-width="0"
          class="register-form"
        >
          <el-form-item prop="username">
            <el-input
              v-model="registerForm.username"
              :placeholder="$t('validation.emailRequired')"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-message"></i>
              </span>
            </el-input>
          </el-form-item>
          <el-form-item prop="emailCode">
            <div class="email-code-row">
              <el-input
                v-model="registerForm.emailCode"
                :placeholder="$t('auth.emailCode')"
                size="large"
                class="custom-input email-code-input"
                maxlength="6"
              >
                <span slot="prefix">
                  <i class="el-icon-key"></i>
                </span>
              </el-input>
              <el-button
                type="primary"
                plain
                size="large"
                class="send-code-btn"
                :loading="sendCodeLoading"
                :disabled="emailCodeCountdown > 0"
                @click="handleSendEmailCode"
              >
                {{
                  sendCodeLoading
                    ? $t('auth.sendingCode')
                    : emailCodeCountdown > 0
                      ? `${emailCodeCountdown}s`
                      : $t('auth.sendEmailCode')
                }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              :type="registerPwdType"
              v-model="registerForm.password"
              :placeholder="$t('auth.password')"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-lock"></i>
              </span>
              <span slot="suffix" @click="toggleRegisterPwd" class="password-toggle">
                <i :class="registerPwdType === 'password' ? 'el-icon-view' : 'el-icon-hide'"></i>
              </span>
            </el-input>
          </el-form-item>
          <el-form-item prop="confirmPassword">
            <el-input
              :type="registerPwdType"
              v-model="registerForm.confirmPassword"
              :placeholder="$t('auth.confirmPassword')"
              @keyup.enter.native="handleRegister"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-lock"></i>
              </span>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="registerForm.telephone"
              :placeholder="$t('auth.phoneOptional')"
              size="large"
              class="custom-input"
            >
              <span slot="prefix">
                <i class="el-icon-phone"></i>
              </span>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              size="large"
              style="width: 100%"
              :loading="registerLoading"
              @click.native.prevent="handleRegister"
              class="login-button"
            >
              {{ registerLoading ? $t('auth.registering') : $t('auth.register') }}
            </el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
    </el-tabs>
  </el-dialog>
</template>

<script>
import { isvalidUsername } from '@/utils/validate';
import { setCookie, getCookie } from '@/utils/support';
import { createAdmin } from '@/api/login';
import registerEmailCode from '@/mixins/registerEmailCode';
import ForgotPasswordForm from '@/components/ForgotPasswordForm';

export default {
  name: 'LoginDialog',
  components: {
    ForgotPasswordForm
  },
  mixins: [registerEmailCode],
  data() {
    const validateUsername = (rule, value, callback) => {
      if (!isvalidUsername(value)) {
        callback(new Error(this.$t('validation.validUsernameRequired')));
      } else {
        callback();
      }
    };
    const validateEmail = (rule, value, callback) => {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!value) {
        callback(new Error(this.$t('validation.emailRequired')));
      } else if (!emailRegex.test(value)) {
        callback(new Error(this.$t('validation.validEmailRequired')));
      } else {
        callback();
      }
    };
    const validatePass = (rule, value, callback) => {
      if (value.length < 3) {
        callback(new Error(this.$t('validation.passwordMinLength')));
      } else {
        callback();
      }
    };
    return {
      dialogVisible: false,
      dialogActiveTab: 'login',
      loginLoading: false,
      registerLoading: false,
      loginPwdType: 'password',
      registerPwdType: 'password',
      isMobile: false,
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [{ required: true, trigger: 'blur', validator: validateUsername }],
        password: [{ required: true, trigger: 'blur', validator: validatePass }]
      },
      registerForm: {
        username: '',
        emailCode: '',
        password: '',
        confirmPassword: '',
        telephone: ''
      },
      registerRules: {
        username: [{ required: true, trigger: 'blur', validator: validateEmail }],
        emailCode: [
          { required: true, message: this.$t('validation.emailCodeRequired'), trigger: 'blur' },
          { pattern: /^\d{6}$/, message: this.$t('validation.emailCodeLength'), trigger: 'blur' }
        ],
        password: [{ required: true, trigger: 'blur', validator: validatePass }],
        confirmPassword: [
          { required: true, message: this.$t('validation.confirmPasswordRequired'), trigger: 'blur' },
          { validator: (rule, value, callback) => {
            if (value !== this.registerForm.password) {
              callback(new Error(this.$t('validation.passwordNotMatch')));
            } else {
              callback();
            }
          }, trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    dialogTitle() {
      if (this.dialogActiveTab === 'forgot') return this.$t('auth.resetPasswordTitle');
      if (this.dialogActiveTab === 'register') return this.$t('auth.register');
      return this.$t('auth.login');
    },
    dialogHeaderTitle() {
      if (this.dialogActiveTab === 'forgot') return this.$t('auth.resetPasswordTitle');
      if (this.dialogActiveTab === 'register') return this.$t('auth.createAccount');
      return this.$t('auth.welcomeBack');
    },
    dialogHeaderSubtitle() {
      if (this.dialogActiveTab === 'forgot') return this.$t('auth.resetPasswordTip');
      if (this.dialogActiveTab === 'register') return this.$t('auth.createAccountTip');
      return this.$t('auth.enterAccount');
    },
    dialogWidth() {
      return this.isMobile ? '92%' : '480px';
    }
  },
  created() {
    this.updateIsMobile();
    this.loginForm.username = getCookie('username') || 'admin';
    this.loginForm.password = getCookie('password') || '';
  },
  mounted() {
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    show() {
      this.updateIsMobile();
      this.dialogVisible = true;
      this.dialogActiveTab = 'login';
    },
    handleClose() {
      // 关闭时重置表单
      if (this.$refs.loginForm) {
        this.$refs.loginForm.resetFields();
      }
      if (this.$refs.registerForm) {
        this.$refs.registerForm.resetFields();
      }
      this.clearEmailCodeTimer();
      this.emailCodeCountdown = 0;
      if (this.$refs.forgotPasswordForm) {
        this.$refs.forgotPasswordForm.reset();
      }
    },
    handleForgotPasswordSuccess(email) {
      this.dialogActiveTab = 'login';
      this.loginForm.username = email;
      this.loginForm.password = '';
      if (this.$refs.forgotPasswordForm) {
        this.$refs.forgotPasswordForm.reset();
      }
    },
    handleTabClick(tab) {
      if (tab.name === 'login') {
        this.$nextTick(() => {
          if (this.$refs.registerForm) {
            this.$refs.registerForm.resetFields();
          }
        });
      } else {
        this.$nextTick(() => {
          if (this.$refs.loginForm) {
            this.$refs.loginForm.resetFields();
          }
        });
      }
    },
    toggleLoginPwd() {
      this.loginPwdType = this.loginPwdType === 'password' ? '' : 'password';
    },
    toggleRegisterPwd() {
      this.registerPwdType = this.registerPwdType === 'password' ? '' : 'password';
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loginLoading = true;
          this.$store.dispatch('Login', this.loginForm).then(() => {
            this.loginLoading = false;
            this.dialogVisible = false;
            setCookie('username', this.loginForm.username, 15);
            setCookie('password', this.loginForm.password, 15);
            this.$message.success(this.$t('tip.loginSuccess'));
            // 触发登录成功事件
            this.$root.$emit('login-success');
            // 刷新页面以更新状态
            this.$nextTick(() => {
              window.location.reload();
            });
          }).catch(() => {
            this.loginLoading = false;
          });
        } else {
          console.log('参数验证不合法！');
          return false;
        }
      });
    },
    handleRegister() {
      this.$refs.registerForm.validate(valid => {
        if (valid) {
          this.registerLoading = true;
          const registerData = {
            username: this.registerForm.username.trim(),
            password: this.registerForm.password,
            telephone: this.registerForm.telephone || '',
            emailCode: this.registerForm.emailCode.trim()
          };
          createAdmin(registerData).then(() => {
            this.registerLoading = false;
            this.$message.success(this.$t('tip.registerSuccessPleaseLogin'));
            this.dialogActiveTab = 'login';
            this.loginForm.username = registerData.username;
            this.loginForm.password = '';
            this.clearEmailCodeTimer();
            this.emailCodeCountdown = 0;
            this.$refs.registerForm.resetFields();
          }).catch(() => {
            this.registerLoading = false;
          });
        } else {
          console.log('参数验证不合法！');
          return false;
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.login-dialog {
  ::v-deep .el-dialog {
    border-radius: 12px;
    overflow: hidden;
  }
  
  ::v-deep .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }
  
  ::v-deep .el-dialog__body {
    padding: 0;
  }
  
  ::v-deep .el-dialog__headerbtn {
    top: 15px;
    right: 15px;
    z-index: 10;
  }
  
  .login-header {
    background: linear-gradient(135deg, #DC143C 0%, #B22222 100%);
    padding: 40px 30px 30px;
    text-align: center;
    color: #fff;
    
    .logo-container {
      .logo-text {
        font-size: 32px;
        font-weight: bold;
        letter-spacing: 2px;
        margin: 0 auto 20px;
        color: #fff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      .login-title {
        font-size: 24px;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: #fff;
      }
      
      .login-subtitle {
        font-size: 14px;
        margin: 0;
        opacity: 0.9;
        color: #fff;
      }
    }
  }
  
  .login-tabs {
    padding: 0 30px 30px;
    
    ::v-deep .el-tabs__header {
      margin: 20px 0 30px;
    }
    
    ::v-deep .el-tabs__nav-wrap::after {
      height: 1px;
      background-color: #e4e7ed;
    }
    
      ::v-deep .el-tabs__item {
        font-size: 16px;
        font-weight: 500;
        padding: 0 20px;
        height: 50px;
        line-height: 50px;
        
        &.is-active {
          color: #DC143C;
          font-weight: 600;
        }
      }
    
    ::v-deep .el-tabs__active-bar {
      background-color: #DC143C;
      height: 3px;
    }
  }

  .login-form,
  .register-form {
    ::v-deep .el-form-item {
      margin-bottom: 24px;
    }

    .form-extra {
      display: flex;
      justify-content: flex-end;
      margin: -14px 0 10px;

      .forgot-btn {
        padding: 0;
        font-size: 13px;
        color: #909399;

        &:hover {
          color: #DC143C;
        }
      }
    }
    
    .custom-input {
      ::v-deep .el-input__inner {
        height: 48px;
        line-height: 48px;
        border-radius: 8px;
        border: 1px solid #e4e7ed;
        padding-left: 45px;
        padding-right: 40px;
        font-size: 14px;
        transition: all 0.3s;
        
        &:focus {
          border-color: #DC143C;
          box-shadow: 0 0 0 2px rgba(220, 20, 60, 0.1);
        }
      }
      
      ::v-deep .el-input__prefix {
        left: 15px;
        i {
          font-size: 18px;
          color: #909399;
          transition: color 0.3s;
        }
      }
      
      ::v-deep .el-input__inner:focus + .el-input__prefix i {
        color: #DC143C;
      }
      
      .password-toggle {
        cursor: pointer;
        color: #909399;
        transition: color 0.3s;
        
        &:hover {
          color: #DC143C;
        }
        
        i {
          font-size: 18px;
        }
      }
    }
    
    .login-button {
      height: 48px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      width: 100%;
      background: linear-gradient(135deg, #DC143C 0%, #B22222 100%);
      border: none;
      transition: all 0.3s;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(220, 20, 60, 0.4);
      }
      
      &:active {
        transform: translateY(0);
      }
    }

    .email-code-row {
      display: flex;
      gap: 10px;
      align-items: stretch;

      .email-code-input {
        flex: 1;
        min-width: 0;
      }

      .send-code-btn {
        flex-shrink: 0;
        min-width: 108px;
        height: 48px;
        padding: 0 12px;
        border-radius: 8px;
        color: #DC143C;
        border-color: rgba(220, 20, 60, 0.45);

        &:hover,
        &:focus {
          color: #DC143C;
          border-color: #DC143C;
          background: rgba(220, 20, 60, 0.06);
        }

        &.is-disabled {
          color: #909399;
          border-color: #e4e7ed;
        }
      }
    }
  }
  
  .color-main {
    color: #DC143C;
  }
}

@media (max-width: 768px) {
  .login-dialog {
    ::v-deep .el-dialog,
    ::v-deep .login-dialog-panel {
      margin: 3vh auto !important;
      max-height: 94vh;
      border-radius: 10px;
    }

    ::v-deep .el-dialog__headerbtn {
      top: 12px;
      right: 12px;
    }

    .login-header {
      padding: 28px 20px 20px;

      .logo-container {
        .logo-text {
          font-size: 26px;
          margin-bottom: 12px;
          letter-spacing: 1px;
        }

        .login-title {
          font-size: 20px;
        }

        .login-subtitle {
          font-size: 13px;
        }
      }
    }

    .login-tabs {
      padding: 0 16px 20px;

      ::v-deep .el-tabs__header {
        margin: 12px 0 18px;
      }

      ::v-deep .el-tabs__item {
        font-size: 15px;
        padding: 0 12px;
        height: 44px;
        line-height: 44px;
      }
    }

    .login-form,
    .register-form {
      ::v-deep .el-form-item {
        margin-bottom: 16px;
      }

      .form-extra {
        margin: -8px 0 8px;
      }

      .custom-input {
        ::v-deep .el-input__inner {
          height: 44px;
          line-height: 44px;
          padding-left: 40px;
          font-size: 16px; // 避免 iOS 自动放大
        }

        ::v-deep .el-input__prefix {
          left: 12px;

          i {
            font-size: 16px;
          }
        }
      }

      .login-button {
        height: 44px;
        font-size: 15px;

        &:hover {
          transform: none;
          box-shadow: none;
        }
      }

      .email-code-row {
        flex-direction: column;
        gap: 8px;

        .send-code-btn {
          width: 100%;
          min-width: 0;
          height: 44px;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .login-dialog {
    .login-header {
      padding: 22px 14px 16px;

      .logo-container {
        .logo-text {
          font-size: 22px;
          margin-bottom: 10px;
        }

        .login-title {
          font-size: 18px;
        }
      }
    }

    .login-tabs {
      padding: 0 12px 16px;

      ::v-deep .el-tabs__item {
        padding: 0 8px;
        font-size: 14px;
      }
    }
  }
}
</style>

<!-- append-to-body 后兜底样式，确保挂到 body 上也能生效 -->
<style lang="scss">
@media (max-width: 768px) {
  .login-dialog-panel {
    width: 92% !important;
    margin: 3vh auto !important;
    max-height: 94vh;
    display: flex;
    flex-direction: column;

    .el-dialog__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/LoginDialog.vue